import { getCustomProps } from '../forms'
import { getData } from '../get-table'
import { HttpService } from '../http.service'

const http = new HttpService()

export interface Incident {
  id?: string | null
  amount?: number | string | null
  comment?: string | null
  conceptId?: string | null
  payrollId?: string | null
  conceptCode?: string | null
  conceptName?: string | null
  costCenterId?: string | null
  creditAccountCode?: string | null
  currencyId?: string | null
  debitAccountCode?: string | null
  employeeCode?: string | null
  employeeId?: string | null
  employeeName?: string | null
  endDate: string | null
  frequencyCode?: string | null
  payrollCode?: string | null
  percentage?: number | string | null
  quantity?: number | string | null
  reference?: string | null
  closeId?: string | null
  state?: string | null
  startDate: string | null
  processId?: string | null
  debitAccountId?: string | null
  creditAccountId?: string | null
  typeProcessCode?: string | null
  fields?: string[]
  offset?: number
  limit?: number
  customFields?: string[]
  realDate?: string | null
}

export interface DeleteIncidentRequests {
  id?: string
  user?: string
  observation?: string
  incidents?: any[]
  status?: string
  errors?: any[]
}

export async function getDeleteIncidentRequests(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = ''
): Promise<any> {
  // where's
  const where = ['payroll_id = :payrollId']
  if (search) {
    where.push(
      `CONCAT(delete_incident_requests.user,observation,process_status.name,errors) ILIKE '%${search}%'`
    )
  }
  // order by
  let modifier = []
  dataOrder !== ''
    ? (modifier = [`order by ${dataOrder[1]} ${dataOrder[0]}`])
    : (modifier = ['order by delete_incident_requests.updated_at desc'])
  // result
  const response: any = await getData({
    tables: ['pg_delete_incident_requests'],
    fields: [
      'delete_incident_requests.id',
      'delete_incident_requests.user as name_user',
      'observation',
      'incidents',
      'process_status.name as status_name',
      'process_status.code as status_code',
      'errors',
      'delete_incident_requests.updated_at',
      'delete_incident_requests.successful_rows'
    ],
    join: [
      'INNER JOIN process_status ON delete_incident_requests.status_id = process_status.id'
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response[0]
}

export async function getIncidents(
  offset: number,
  limit: number,
  startDate: string,
  endDate: string,
  employeeId: any = '',
  conceptId: any = '',
  processId: any = '',
  reference: any = undefined,
  closeId: any = undefined
): Promise<any> {
  let fields
  if (limit !== 10000) {
    fields = [
      'amount',
      'closeId',
      'reference',
      'conceptCode',
      'conceptName',
      'employeeCode',
      'employeeName',
      'endDate',
      'startDate',
      'id',
      'percentage',
      'quantity',
      'typeProcessCode'
    ]
  } else {
    fields = ['id', 'closeId', 'reference']
  }
  const data: Incident = {
    startDate,
    endDate,
    fields,
    offset,
    limit
  }
  if (employeeId !== '') data.employeeId = employeeId
  if (conceptId !== '') data.conceptId = conceptId
  if (processId !== '') data.typeProcessCode = processId
  if (reference !== undefined) data.reference = reference
  if (closeId !== undefined) data.closeId = closeId

  const response: any = await http.post('calculation/incidents/list-all', data)
  return response.data.data
}

export async function getListEmployees(payrollId: string) {
  const response: any = await getData({
    tables: ['pg_employees'],
    fields: ['id', 'code', "CONCAT(code,' - ', name) as name"],
    where: [`payroll_id = '${payrollId}'`],
    modifier: ['order by code desc']
  })
  return response[0].results
}

export async function getListConcepts(payrollId: string) {
  const response: any = await getData({
    tables: ['pg_concepts'],
    fields: ['id', 'code', "CONCAT(code,' - ', name) as name"],
    where: [`payroll_id = '${payrollId}'`],
    modifier: ['order by code desc']
  })
  return response[0].results
}

export async function getListProcesses(countryId: string) {
  const response: any = await getData({
    tables: ['pg_processes'],
    fields: ['id', 'code', "name"],
    where: [`processes.countries ?| array['${countryId}']`],
    modifier: ['order by code asc']
  })
  return response[0].results
}

export async function getPayroll(payrollId: string) {
  const response: any = await getData({
    tables: ['pg_payrolls'],
    fields: ['payrolls.last_payment', 'frequencies.name as frequency'],
    join: ['INNER JOIN frequencies ON frequencies.id = payrolls.frequency_id'],
    where: [`payrolls.id = '${payrollId}'`]
  })
  return response[0].results[0]
}

export async function getDatesCalendar(payrollId: string, lastPayment: string) {
  const response: any = await getData({
    tables: ['pg_calendars'],
    fields: ['start_date', 'end_date'],
    where: [
      `end_date > DATE('${lastPayment}')`,
      `payroll_id = '${payrollId}'`,
      'is_deleted = false'
    ]
  })
  return response[0].results[0]
}

export async function getAccounts(conceptId: string) {
  const response: any = await getData({
    tables: ['pg_concepts'],
    fields: ['credit_account_id', 'debit_account_id'],
    where: [`id = '${conceptId}'`]
  })
  return response[0].results[0]
}

export async function getIncident(id: string): Promise<boolean> {
  const response: any = await http.get(`calculation/incidents/${id}`)
  return response.data
}

export async function createIncident(data: Incident): Promise<boolean> {
  const info: Incident = {
    startDate: data.startDate,
    endDate: data.endDate,
    payrollId: data.payrollId,
    employeeId: data.employeeId,
    processId: data.processId,
    conceptId: data.conceptId,
    customFields: {
      ...getCustomProps(data)
    },
    realDate: data.realDate || data.startDate
  }
  if (data.costCenterId !== '') info.costCenterId = data.costCenterId
  if (data.debitAccountId !== '') info.debitAccountId = data.debitAccountId
  if (data.creditAccountId !== '') info.creditAccountId = data.creditAccountId
  if (data.percentage !== '') info.percentage = Number(data.percentage)
  if (data.amount !== '') info.amount = Number(data.amount)
  if (data.quantity !== '') info.quantity = Number(data.quantity)
  if (data.currencyId !== '') info.currencyId = data.currencyId
  if (data.comment !== '') info.comment = data.comment
  const response: any = await http.post('calculation/incidents', info)

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function updateIncident(
  data: Incident,
  id: string
): Promise<boolean> {
  const info: Incident = {
    startDate: data.startDate,
    endDate: data.endDate,
    payrollId: data.payrollId,
    employeeId: data.employeeId,
    processId: data.processId,
    conceptId: data.conceptId,
    customFields: {
      ...getCustomProps(data)
    },
    realDate: data.realDate || data.startDate
  }
  if (data.costCenterId !== '') info.costCenterId = data.costCenterId
  if (data.debitAccountId !== '') info.debitAccountId = data.debitAccountId
  if (data.creditAccountId !== '') info.creditAccountId = data.creditAccountId
  if (data.percentage !== '') info.percentage = Number(data.percentage)
  if (data.amount !== '') info.amount = Number(data.amount)
  if (data.quantity !== '') info.quantity = Number(data.quantity)
  if (data.currencyId !== '') info.currencyId = data.currencyId
  if (data.comment !== '') info.comment = data.comment
  const response: any = await http.put('calculation/incidents/' + id, info)

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function deleteIncident(id: string): Promise<boolean> {
  const response: any = await http.delete('calculation/incidents/' + id)

  if (response?.data?.data?.error) {
    return false
  }

  return true
}

export async function deleteIncidents(data: any): Promise<boolean> {
  const response: any = await http.post(
    'calculation/incidents/bulk-delete/',
    data
  )

  if (response?.data?.data?.error) {
    return false
  }

  return true
}
